import React from 'react'
import { Box, Paper, Typography, Chip } from '@mui/material'
import Check from '@mui/icons-material/Check'
import Cross from '@mui/icons-material/Cancel';
import { useAPI } from 'contexts/APIProvider'
import { useFirebase } from 'contexts/FirebaseProvider'
import Nurse from 'logic/Nurse'
import CheckboxSelectField, { CheckboxSelectOption } from 'components/CheckboxSelectField'
import { Experience, DocumentFileType } from '../types/types'
import NurseDocumentFile from 'logic/NurseDocumentFile'
import { v4 as uuidv4 } from 'uuid'
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

interface ExperienceFormProps {
  nurseId: number
  experienceType: 'implant' | 'orthodontics'
}

const ExperienceForm: React.FC<ExperienceFormProps> = ({ nurseId, experienceType }) => {
  const { api } = useAPI()
  const { storage } = useFirebase()
  const [nurse, setNurse] = React.useState<Nurse>()
  const [experience, setExperience] = React.useState<Experience>('no')
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string>('')
  const [files, setFiles] = React.useState<NurseDocumentFile[]>([])
  const [uploading, setUploading] = React.useState<boolean>(false)
  const [deleting, setDeleting] = React.useState<boolean>(false)

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const fieldName = `${experienceType}_experience` as 'implant_experience' | 'orthodontics_experience'
  
  const documentType: DocumentFileType = React.useMemo(() => {
    if (experienceType === 'implant') {
        return 'implant_certificate'
    } else {
        return 'orthodontics_certificate'
    }
  }, [experienceType])

  const experienceOptions: CheckboxSelectOption[] = [
    { value: 'no', label: 'No' },
    { value: 'yes', label: 'Yes' },
    { value: 'certified', label: 'Yes and I am certified' }
  ]

  const fetchNurse = React.useCallback(async () => {
    try {
      const nurseData = await api.getNurse(nurseId)
      setNurse(nurseData)
      if (nurseData?.[fieldName]) {
        setExperience(nurseData[fieldName])
      }
    } catch (e) {
      setError('Error loading nurse experience')
      console.error(e)
    }
  }, [api, nurseId, fieldName])

  const fetchDocuments = React.useCallback(async () => {
    try {
      const documents = await api.listDocuments({ nurse_id: nurseId, doc_type: documentType })
      setFiles(documents)
    } catch (e) {
      console.error('Error fetching documents:', e)
    }
  }, [api, nurseId, documentType])

  const reload = React.useCallback(async () => {
    await fetchNurse()
    await fetchDocuments()
  }, [fetchNurse, fetchDocuments])

  const handleExperienceChange = (value: string | number) => {
    const newValue = value as Experience
    setExperience(newValue)
    saveExperience(newValue)
  }

  const saveExperience = React.useCallback(async (expValue: Experience) => {
    try {
      setLoading(true)
      const data = { [fieldName]: expValue }
      await api.editNurse(data, nurseId)
      setError('')
    } catch (e) {
      setError('Error saving nurse experience')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, nurseId, fieldName])

  const handleFileInputChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !nurse) return

    try {
      setUploading(true)
      
      // Upload to Firebase
      const key = uuidv4()
      const fileRef = ref(storage, `${documentType}/${nurse.nurse_id}/${key}`)
      const metadata = {
        contentType: file.type,
        cacheControl: 'no-store',
      }
      
      await uploadBytes(fileRef, file, metadata)
      await getDownloadURL(fileRef) // We don't need the URL, but this confirms upload is complete
      
      // Create document in API
      await api.createDocument({
        nurse_id: nurse.nurse_id,
        doc_type: documentType,
        key: key,
        page_num: (files.length || 0) + 1,
        content_type: file.type,
      })
      
      // Reset input and reload
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      await reload()
    } catch (e) {
      setError('Error uploading file')
      console.error(e)
    } finally {
      setUploading(false)
    }
  }, [nurse, storage, documentType, files.length, api, reload])

  const deleteDocument = React.useCallback(async (fileId: number) => {
    try {
      setDeleting(true)
      await api.deleteDocument(fileId)
      await reload()
    } catch (e) {
      setError('Error deleting document')
      console.error(e)
    } finally {
      setDeleting(false)
    }
  }, [api, reload])

  const backgroundColor = React.useMemo(() => {
    if (experience === 'no') {
      return 'default'
    } else {
      return 'success.light'
    }
  }, [experience])

  const body = React.useMemo(() => {
    const typeLabel = experienceType === 'implant' ? 'implant' : 'orthodontics'
    return (
      <Typography variant='body2'>
        Do you have working experience with {typeLabel}?
      </Typography>
    )
  }, [experienceType])

  const icon = React.useMemo(() => {
    if (experience === 'no') {
      return <Cross fontSize='large' sx={{ color: 'default' }} />
    } else {
      return <Check fontSize='large' sx={{ color: 'success.main' }} />
    }
  }, [experience])

  React.useEffect(() => {
    fetchNurse()
  }, [fetchNurse])

  React.useEffect(() => {
    if (experience === 'certified') {
      fetchDocuments()
    }
  }, [experience, fetchDocuments])

  // Hidden file input element
  const fileInput = (
    <input 
      type="file"
      accept="image/*, application/pdf"
      onChange={handleFileInputChange}
      ref={fileInputRef}
      style={{ display: 'none' }}
    />
  );

  return (
    <Box>
      <Paper sx={{
        padding: 1,
        display: 'grid',
        gridTemplateColumns: icon ? ['1fr 4fr', '1fr 9fr'] : '0 1fr',
        gridTemplateRows: 'auto',
        gridTemplateAreas: `
          "icon title"
          "icon content"
          "icon select"
          "icon upload"`,
        backgroundColor: backgroundColor,
      }}>
        <Box sx={{ gridArea: 'icon', justifySelf: 'center', alignSelf: 'center' }}>
          {icon}
        </Box>
        <Typography sx={{ gridArea: 'title' }} variant='h5'>
          {experienceType === 'implant' ? 'Implant' : 'Orthodontics'} Experience
        </Typography>
        <Box sx={{ gridArea: 'content' }}>
          {body}
        </Box>
        <Box sx={{ gridArea: 'select', display: 'flex', flexDirection: 'column' }}>
          <CheckboxSelectField
            value={experience}
            options={experienceOptions}
            onChange={handleExperienceChange}
            sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
          />
          {error && (
            <Typography variant='body2' color='error'>
              {error}
            </Typography>
          )}
        </Box>
        {experience === 'certified' && (
          <Box sx={{ gridArea: 'upload', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>Certification:</Typography>
            
            {files.map(file => (
              <Chip
                key={file.id}
                label={deleting ? 'Deleting...' : `Page ${file.page_num}`}
                size="small"
                variant="outlined"
                icon={<DeleteIcon />}
                onClick={() => deleteDocument(file.id)}
                disabled={deleting}
              />
            ))}
            
            <Chip
              label={uploading ? 'Uploading...' : 'Upload Certificate'}
              size="small"
              color="primary"
              icon={<AddIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            />
            
            {fileInput}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default ExperienceForm
